import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /**
   * Prevent occasional double `cart:update` delta events (e.g. 5 -> 7) from causing the bubble to drift.
   * @type {number}
   */
  #optimisticLockUntil = 0;

  /** @type {number | null} */
  #reconcileTimer = null;

  /** @type {number} */
  get currentCartCount() {
    const parsed = parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);

    if (this.#reconcileTimer != null) {
      window.clearTimeout(this.#reconcileTimer);
      this.#reconcileTimer = null;
    }
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    if (comingFromProductForm) {
      const didError = event.detail.data?.didError === true;

      // If there was an error, don't do optimistic math—just reconcile.
      if (!didError) {
        // Prevent occasional duplicate delta events from double-incrementing the bubble.
        const now = Date.now();
        if (now < this.#optimisticLockUntil) return;
        this.#optimisticLockUntil = now + 800;

        // Optimistic delta update
        this.renderCartBubble(itemCount, true);
      }

      // Always reconcile to the true cart total shortly after product-form updates
      if (this.#reconcileTimer != null) window.clearTimeout(this.#reconcileTimer);
      this.#reconcileTimer = window.setTimeout(async () => {
        try {
          const cart = await fetch('/cart.js').then((r) => r.json());
          const total = Number(cart?.item_count) || 0;
          this.renderCartBubble(total, false, false);
        } catch (_) {
          // no-op
        }
      }, 250);

      return;
    }

    // Non-product-form updates are treated as absolute totals
    this.renderCartBubble(itemCount, false);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    const currentCount = this.currentCartCount;
    const nextCount = comingFromProductForm ? currentCount + itemCount : itemCount;
  
    // If nothing changes, don't re-animate / re-write DOM (prevents "rebuy second update")
    if (nextCount === currentCount) return;
  
    // Use nextCount for all UI state (not itemCount)
    this.refs.cartBubbleCount.classList.toggle('hidden', nextCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', nextCount === 0);
  
    const shouldAnimate = animate && nextCount > 0;
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', shouldAnimate);
  
    this.currentCartCount = nextCount;
  
    this.classList.toggle('header-actions__cart-icon--has-cart', nextCount > 0);
  
    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );
  
    if (!shouldAnimate) return;
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
